import React, { useState, useRef, useEffect } from 'react';
import { ViewType } from '../types';
import { Gamepad2, ChevronDown, Flame, Zap, Sparkles } from 'lucide-react';

interface HeaderProps {
  currentView: ViewType;
  onNavigate: (view: ViewType) => void;
  onScrollToGame?: (game: 'tower' | 'draw' | 'puzzle') => void;
}

export const Header: React.FC<HeaderProps> = ({
  currentView,
  onNavigate,
  onScrollToGame,
}) => {
  const [isGamesDropdownOpen, setIsGamesDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Detect scroll position to elevate header background
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsGamesDropdownOpen(false);
      }
    };
    if (isGamesDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isGamesDropdownOpen]);

  const handleSelectGame = (game: 'tower' | 'draw' | 'puzzle') => {
    setIsGamesDropdownOpen(false);
    if (onScrollToGame) {
      onScrollToGame(game);
    } else {
      const targetId = `game-${game}`;
      if (currentView !== 'home') {
        onNavigate('home');
        setTimeout(() => {
          const el = document.getElementById(targetId);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            el.classList.add('ring-4', 'ring-[#ffd166]', 'shadow-[0_0_30px_rgba(255,209,102,0.6)]');
            setTimeout(() => el.classList.remove('ring-4', 'ring-[#ffd166]', 'shadow-[0_0_30px_rgba(255,209,102,0.6)]'), 2000);
          }
        }, 150);
      } else {
        const el = document.getElementById(targetId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          el.classList.add('ring-4', 'ring-[#ffd166]', 'shadow-[0_0_30px_rgba(255,209,102,0.6)]');
          setTimeout(() => el.classList.remove('ring-4', 'ring-[#ffd166]', 'shadow-[0_0_30px_rgba(255,209,102,0.6)]'), 2000);
        }
      }
    }
  };

  return (
    <header
      className={`world-header fixed top-0 left-0 right-0 z-40 w-full transition-all duration-300 ${
        currentView !== 'home' || isScrolled
          ? 'topbar-v2 bg-[#170c1b]/95 backdrop-blur-md shadow-[0_8px_32px_rgba(0,0,0,0.7)] border-b border-[#ffb703]/35'
          : 'bg-gradient-to-b from-[#150a18]/95 via-[#150a18]/70 to-transparent border-b border-[#ffb703]/25 backdrop-blur-[4px]'
      }`}
    >
      {/* Brand logo + Teen Arena Badge */}
      <button
        type="button"
        onClick={() => {
          if (currentView === 'home') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            onNavigate('home');
          }
        }}
        className="brand text-left shrink-0 group"
        aria-label="VNFAM Play — Trang chủ"
      >
        <img
          src="/images/logo/vnfam-logo-horizontal-transparent.webp"
          alt="Bảo tàng Mỹ thuật Việt Nam"
          width="136"
          className="h-auto group-hover:scale-105 transition-transform"
        />
        <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-[#ff3366]/20 to-[#ffb703]/20 border border-[#ffd166]/50 shadow-sm">
          <Sparkles className="w-3 h-3 text-[#ffd166] animate-spin" style={{ animationDuration: '4s' }} />
          <span className="text-[10px] font-black tracking-wider text-[#ffd166] uppercase">
            TEEN ARENA
          </span>
        </div>
      </button>

      {/* Center Navigation Links (desktop) */}
      <nav aria-label="Điều hướng" className="hidden md:flex items-center gap-2 lg:gap-4 ml-auto mr-4">
        <button
          type="button"
          onClick={() => {
            if (currentView === 'home') {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              onNavigate('home');
            }
          }}
          className={`px-3 py-1.5 rounded-xl font-extrabold text-sm flex items-center gap-1.5 transition-all ${
            currentView === 'home'
              ? 'bg-[#ffb703]/20 text-[#ffd166] border border-[#ffb703]/50 shadow-[0_0_12px_rgba(255,183,3,0.3)]'
              : 'text-[#f0e1d2] hover:text-white hover:bg-white/10'
          }`}
          aria-current={currentView === 'home' ? 'page' : undefined}
        >
          <span>🚀</span>
          <span>Đấu trường</span>
        </button>

        <button
          type="button"
          onClick={() => onNavigate('journey')}
          className={`px-3 py-1.5 rounded-xl font-extrabold text-sm flex items-center gap-1.5 transition-all ${
            currentView === 'journey'
              ? 'bg-[#ff3366]/20 text-[#ff708f] border border-[#ff3366]/50 shadow-[0_0_12px_rgba(255,51,102,0.3)]'
              : 'text-[#f0e1d2] hover:text-white hover:bg-white/10'
          }`}
          aria-current={currentView === 'journey' ? 'page' : undefined}
        >
          <span>🎒</span>
          <span>Balo Săn Tem</span>
        </button>

        <button
          type="button"
          onClick={() => onNavigate('summary')}
          className={`px-3 py-1.5 rounded-xl font-extrabold text-sm flex items-center gap-1.5 transition-all ${
            currentView === 'summary'
              ? 'bg-[#8b5cf6]/20 text-[#c084fc] border border-[#8b5cf6]/50 shadow-[0_0_12px_rgba(139,92,246,0.3)]'
              : 'text-[#f0e1d2] hover:text-white hover:bg-white/10'
          }`}
          aria-current={currentView === 'summary' ? 'page' : undefined}
        >
          <span>🪪</span>
          <span>Thẻ Kiện Tướng</span>
        </button>
      </nav>

      {/* GAMER STATUS HUD & QUICK GAME SELECTOR */}
      <div className="flex items-center gap-2.5 ml-auto md:ml-0" ref={dropdownRef}>
        {/* Teen Gamer Status Mini HUD (desktop) */}
        <div className="hidden xl:flex items-center gap-2 bg-black/40 border border-[#ffb703]/30 rounded-full px-3 py-1 shadow-inner backdrop-blur-sm">
          {/* Streak */}
          <div className="flex items-center gap-1 text-xs font-black text-[#ffedd5] bg-[#ff3366]/20 border border-[#ff3366]/40 px-2 py-0.5 rounded-full" title="Chuỗi 3 ngày tham gia liên tiếp!">
            <Flame className="w-3.5 h-3.5 text-[#ff4d6d] fill-current animate-bounce" />
            <span>3 ngày</span>
          </div>

          {/* Level / XP */}
          <div className="flex items-center gap-1 text-xs font-black text-[#ffd166] bg-[#ffb703]/20 border border-[#ffb703]/40 px-2 py-0.5 rounded-full" title="Level 5 · 850 XP">
            <Zap className="w-3.5 h-3.5 text-[#ffb703] fill-current" />
            <span>Cấp 5</span>
          </div>
        </div>

        {/* Desktop Quick Game Buttons */}
        <div className="hidden lg:flex items-center gap-1 bg-[#281330]/80 border border-[#ffb703]/40 rounded-full p-1 shadow-md backdrop-blur-sm">
          <span className="text-[10px] font-black text-[#ffd166] px-1.5 uppercase tracking-wider flex items-center gap-1">
            <Gamepad2 className="w-3.5 h-3.5 text-[#ffd166]" />
            Game:
          </span>
          <button
            type="button"
            onClick={() => handleSelectGame('tower')}
            className="px-2.5 py-1 rounded-full text-xs font-black text-[#fff8ee] hover:text-[#ffd166] hover:bg-[#ff3366]/25 transition-all flex items-center gap-1 active:scale-95"
            title="Cuộn nhanh tới Leo Tháp Mỹ Thuật"
          >
            <span>🏰</span>
            <span>Leo tháp</span>
          </button>
          <button
            type="button"
            onClick={() => handleSelectGame('draw')}
            className="px-2.5 py-1 rounded-full text-xs font-black text-[#fff8ee] hover:text-[#06d6a0] hover:bg-[#06d6a0]/25 transition-all flex items-center gap-1 active:scale-95"
            title="Cuộn nhanh tới Họa Sĩ Bất Đắc Dĩ"
          >
            <span>🎨</span>
            <span>Vẽ & đoán</span>
          </button>
          <button
            type="button"
            onClick={() => handleSelectGame('puzzle')}
            className="px-2.5 py-1 rounded-full text-xs font-black text-[#fff8ee] hover:text-[#c084fc] hover:bg-[#8b5cf6]/25 transition-all flex items-center gap-1 active:scale-95"
            title="Cuộn nhanh tới Ghép Tranh Tốc Độ"
          >
            <span>🧩</span>
            <span>Ghép tranh</span>
          </button>
        </div>

        {/* Mobile & Compact Screen Dropdown Button */}
        <div className="relative lg:hidden">
          <button
            type="button"
            onClick={() => setIsGamesDropdownOpen((prev) => !prev)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#e11d48] to-[#9f1239] text-[#ffd166] border-2 border-[#ffb703]/70 shadow-lg text-xs font-black active:scale-95 transition-all"
            aria-expanded={isGamesDropdownOpen}
            aria-label="Chọn nhanh trò chơi để cuộn xuống dưới"
          >
            <Gamepad2 className="w-3.5 h-3.5 text-[#ffd166]" />
            <span className="text-[11px] font-black">3 Game</span>
            <ChevronDown className={`w-3 h-3 text-[#ffd166] transition-transform ${isGamesDropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Quick Dropdown Menu */}
          {isGamesDropdownOpen && (
            <div className="absolute right-0 top-full mt-2 w-64 bg-[#23112c]/98 backdrop-blur-xl border-2 border-[#ffb703]/60 rounded-2xl p-2 shadow-2xl z-50 animate-fade-in space-y-1.5">
              <div className="text-[9px] font-black text-[#ffd166] uppercase tracking-wider px-2 py-1 flex items-center justify-between border-b border-[#ffb703]/20 pb-1.5">
                <span>Chọn trò chơi</span>
                <span className="text-[#06d6a0]">Cuộn xuống ↓</span>
              </div>

              <button
                type="button"
                onClick={() => handleSelectGame('tower')}
                className="w-full text-left p-2 rounded-xl hover:bg-[#ff3366]/20 flex items-center gap-2.5 text-xs text-[#fff8ee] transition-all group active:scale-98"
              >
                <span className="w-9 h-9 rounded-xl bg-[#59142b] border border-[#ff3366]/50 flex items-center justify-center text-lg group-hover:scale-110 transition-transform shrink-0 shadow-md">
                  🏰
                </span>
                <div className="min-w-0">
                  <div className="font-black text-[#ffd166] group-hover:text-white">01 · Leo tháp mỹ thuật</div>
                  <div className="text-[10.5px] text-[#fcd7b8]/80 truncate">10 tầng câu đố hack não</div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handleSelectGame('draw')}
                className="w-full text-left p-2 rounded-xl hover:bg-[#06d6a0]/20 flex items-center gap-2.5 text-xs text-[#fff8ee] transition-all group active:scale-98"
              >
                <span className="w-9 h-9 rounded-xl bg-[#093b32] border border-[#06d6a0]/50 flex items-center justify-center text-lg group-hover:scale-110 transition-transform shrink-0 shadow-md">
                  🎨
                </span>
                <div className="min-w-0">
                  <div className="font-black text-[#06d6a0] group-hover:text-white">02 · Vẽ & đoán tác phẩm</div>
                  <div className="text-[10.5px] text-[#fcd7b8]/80 truncate">Bảng màu sơn mài siêu hài</div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handleSelectGame('puzzle')}
                className="w-full text-left p-2 rounded-xl hover:bg-[#8b5cf6]/20 flex items-center gap-2.5 text-xs text-[#fff8ee] transition-all group active:scale-98"
              >
                <span className="w-9 h-9 rounded-xl bg-[#3f1958] border border-[#8b5cf6]/50 flex items-center justify-center text-lg group-hover:scale-110 transition-transform shrink-0 shadow-md">
                  🧩
                </span>
                <div className="min-w-0">
                  <div className="font-black text-[#c084fc] group-hover:text-white">03 · Ghép tranh di sản</div>
                  <div className="text-[10.5px] text-[#fcd7b8]/80 truncate">Phục chế 6 Bảo vật Quốc gia</div>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
